<template>
    <v-row justify="center" align="center">
        <v-col cols="12" sm="12" md="12">
            <v-row>
                <v-col cols="12" sm="12" md="8">
                    <h1>Gross Profit</h1>
                </v-col>
                <v-col cols="12" sm="12" md="4">
                    <v-text-field
                        v-model="data.quickSearchVal"
                        :label="CommonLabelTextHelper.getLabelText.filterCurrentPage"
                        @keydown="onKeydownQuickSearch"
                    ></v-text-field>
                </v-col>
            </v-row>
            <v-expansion-panels v-model="data.panel" multiple>
                <v-expansion-panel>
                    <v-expansion-panel-title>
                        <h2>Select date range</h2>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                        <date-range-select
                            v-model="data.searchFilterDateRangeGrp.dateOrderOn"
                            :from-label="formLabelText.dateOrderOnFrom"
                            :to-label="formLabelText.dateOrderOnTo"
                            :enable-validation="true"
                            @validation-change="onDateRangeValidationChange"
                        />
                        <v-row class="mt-2">
                            <v-btn class="mr-2" variant="text" @click="onResetClick">
                                <v-icon>mdi-cached</v-icon>
                                Clear
                            </v-btn>
                            <v-spacer></v-spacer>
                            <v-btn class="mr-2" variant="text" @click="onSearchClick">
                                <v-icon>mdi-magnify</v-icon>
                                Search
                            </v-btn>
                        </v-row>
                    </v-expansion-panel-text>
                </v-expansion-panel>
            </v-expansion-panels>
            <alert-message ref="errorAlert" :message="data.errorAlertMessage"></alert-message>
            <alert-message ref="paginationErrorAlert" :message="errorMessage"></alert-message>

            <!-- Delete Confirmation Dialog -->
            <v-dialog v-model="data.deleteOperation.showConfirmDialog" max-width="500">
                <v-card>
                    <v-card-title class="headline">
                        <v-icon color="warning" class="mr-2">mdi-alert</v-icon>
                        Confirm Delete
                    </v-card-title>
                    <v-card-text>
                        Are you sure you want to delete this gross profit record?
                        <br /><br />
                        <strong>Gross Profit ID:</strong>
                        {{ data.deleteOperation.selectedGrossProfit?.grossProfitId || '' }}
                        <br />
                        <strong>Order ID:</strong> {{ data.deleteOperation.selectedGrossProfit?.orderId || '' }}
                        <br /><br />
                        <v-alert type="warning" variant="tonal"> This action cannot be undone. </v-alert>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn text @click="onCancelDelete" :disabled="data.deleteOperation.isLoading"> Cancel </v-btn>
                        <v-btn
                            color="error"
                            @click="onConfirmDelete"
                            :loading="data.deleteOperation.isLoading"
                            :disabled="data.deleteOperation.isLoading"
                        >
                            Delete
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <!-- Gross Profit Item Breakdown Dialog -->
            <v-dialog v-model="data.grossProfitItemDialog.showDialog" max-width="900">
                <v-card>
                    <v-card-title class="headline">
                        <v-icon class="mr-2">mdi-chart-box-outline</v-icon>
                        Item-Level Gross Profit Breakdown
                    </v-card-title>
                    <v-card-text>
                        <div v-if="data.grossProfitItemDialog.selectedGrossProfit" class="mb-4">
                            <strong>Order Number:</strong>
                            {{ data.grossProfitItemDialog.selectedGrossProfit.orderNumber }}
                            <br />
                            <strong>Marketplace:</strong>
                            {{ data.grossProfitItemDialog.selectedGrossProfit.marketplaceCode }}
                        </div>
                        <div v-if="data.grossProfitItemDialog.isLoading" class="text-center py-4">
                            <v-progress-circular indeterminate color="primary"></v-progress-circular>
                            <div class="mt-2">Loading item-level gross profit data...</div>
                        </div>
                        <div v-else-if="data.grossProfitItemDialog.items.length === 0" class="text-center py-4">
                            No item-level gross profit records found.
                        </div>
                        <v-table v-else>
                            <thead>
                                <tr>
                                    <th
                                        class="text-left"
                                        v-for="header in grossProfitItemHeaders"
                                        :key="header.value"
                                    >
                                        {{ header.text }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="grossProfitItem in data.grossProfitItemDialog.items"
                                    :key="grossProfitItem.grossProfitItemId"
                                >
                                    <td v-for="header in grossProfitItemHeaders" :key="header.value">
                                        {{ grossProfitItem[header.value] }}
                                    </td>
                                </tr>
                            </tbody>
                        </v-table>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn text @click="onCloseGrossProfitItemsDialog"> Close </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <v-card>
                <v-card-title>
                    <div class="d-flex flex-wrap align-center ga-2 w-100">
                        <v-btn variant="text" @click="onImportClick"> Calculate GP </v-btn>
                        <v-btn variant="text" @click="onSearchClick">
                            <v-icon>mdi-cached</v-icon>
                            Refresh
                        </v-btn>
                        <v-spacer></v-spacer>
                        <export-to-csv
                            :data="currentPageData"
                            :csv-header="data.csvExportConfig.csvHeader"
                            :filename="data.csvExportConfig.filename"
                            :formatter="csvFormatter"
                        ></export-to-csv>
                    </div>
                </v-card-title>

                <v-card-text>
                    <!-- Generic Table Component -->
                    <GenericListTable
                        :pagination="pagination"
                        :current-page-data="currentPageData"
                        :headers="headers"
                        :primary-key="'grossProfitId'"
                        :action-header-value="actionHeaderItem.value"
                        :quick-search-value="data.quickSearchVal"
                        :loading-message="'Loading more gross profit data...'"
                        :is-loading="data.deleteOperation.isLoading"
                        @page-size-change="onPageSizeChange"
                        @page-select="onPageSelect"
                        @next-page="onNextPage"
                        @previous-page="onPreviousPage"
                    >
                        <template #table-actions="{ item, isLoading }">
                            <v-btn
                                :disabled="isLoading"
                                variant="text"
                                icon="mdi-chart-box-outline"
                                @click="onViewGrossProfitItemsClick(item)"
                            >
                            </v-btn>
                            <v-btn
                                :disabled="isLoading"
                                variant="text"
                                icon="mdi-package-variant"
                                @click="onGetOrderItemSkusClick(item)"
                            >
                            </v-btn>
                            <v-btn
                                :disabled="isLoading"
                                :loading="isLoading"
                                variant="text"
                                icon="mdi-delete"
                                color="error"
                                @click="onDeleteGrossProfitClick(item)"
                            >
                            </v-btn>
                        </template>
                    </GenericListTable>
                </v-card-text>
            </v-card>
        </v-col>
    </v-row>
</template>

<script lang="ts">
import { defineComponent, reactive, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import * as _ from 'lodash';
import { fetchWrapper } from '../../utils/FetchWrapper';
import { GrossProfitLabelTextHelper } from '../../utils/labelTextHelper/GrossProfitLabelTextHelper';
import { CommonLabelTextHelper } from '../../utils/labelTextHelper/CommonLabelTextHelper';
import { Formatter } from '../../utils/Formatter';
import { PathDefinition } from '../../utils/PathDefinition';
import DateRangeSelect from '../../components/DateRangeSelect.vue';
import AlertMessage from '../../components/AlertMessage.vue';
import ExportToCsv from '../../components/ExportToCsv.vue';
import GenericListTable from '../../components/GenericListTable.vue';
import { GrossProfitHeaderHelper } from '../../utils/headerHelper/GrossProfitHeaderHelper';
import { CsvFormatHelper } from '../../utils/CsvFormatHelper';
import { usePagination } from '../../composables/usePagination';
import { useUrlFilters } from '../../composables/useUrlFilters';
import { GrossProfitFilterDefinitions } from '../../utils/filterDefinitions/GrossProfitFilterDefinitions';
import { GrossProfitItemHeaderHelper } from '../../utils/headerHelper/GrossProfitItemHeaderHelper';

export default defineComponent({
    name: 'ListGrossProfit',

    components: {
        ExportToCsv,
        AlertMessage,
        GenericListTable,
        DateRangeSelect
    },

    setup() {
        const route = useRoute();

        const data = reactive({
            panel: [0],
            quickSearchVal: '',
            errorAlertMessage: '',
            searchFilterDateRangeGrpDefault: {},
            searchFilterDateRangeGrp: {
                dateOrderOn: {
                    from: null,
                    to: null
                }
            },
            csvExportConfig: {
                filename: 'exportGrossProfit.csv',
                csvHeader: [...GrossProfitHeaderHelper.getCsvHeader()]
            },
            deleteOperation: {
                isLoading: false,
                showConfirmDialog: false,
                selectedGrossProfit: null
            },
            grossProfitItemDialog: {
                showDialog: false,
                isLoading: false,
                selectedGrossProfit: null,
                items: []
            },
            validation: {
                hasErrors: false
            }
        });

        // Initialize URL filter sync
        const { syncFiltersToUrl, restoreFiltersFromUrl, watchRouteChanges } = useUrlFilters({
            filterFields: GrossProfitFilterDefinitions,
            onFiltersRestored: (filters) => {
                // Apply restored filters to reactive state
                Object.keys(filters).forEach((key) => {
                    if (key.startsWith('date') && (key.endsWith('From') || key.endsWith('To'))) {
                        // Handle date range (nested structure)
                        const dateField = key.replace(/From|To$/, '');
                        const direction = key.endsWith('From') ? 'from' : 'to';
                        data.searchFilterDateRangeGrp[dateField][direction] = filters[key];
                    }
                });
            }
        });

        // Initialize pagination
        const {
            pagination,
            currentPageData,
            errorMessage,
            loadPage,
            resetPagination,
            nextPage,
            previousPage,
            selectPage,
            changePageSize,
            removeItemFromAllPages,
            updateItemInAllPages,
            initialize
        } = usePagination({
            apiEndpoint: '/api/staff/grossProfit/list',
            buildApiParams: (page: number) => {
                const params: any = {};

                // Add date range filtering
                if (data.searchFilterDateRangeGrp.dateOrderOn.from) {
                    params.dateOrderOnFrom = Formatter.convertDateFromInputToUtcDateStr(
                        data.searchFilterDateRangeGrp.dateOrderOn.from
                    );
                }

                if (data.searchFilterDateRangeGrp.dateOrderOn.to) {
                    params.dateOrderOnTo = Formatter.convertDateToInputToUtcDateStr(
                        data.searchFilterDateRangeGrp.dateOrderOn.to
                    );
                }

                return params;
            }
        });

        onMounted(async () => {
            // Clone defaults BEFORE restoring from URL
            data.searchFilterDateRangeGrpDefault = _.cloneDeep(data.searchFilterDateRangeGrp);

            // Restore filters from URL (if any)
            const hasUrlFilters = Object.keys(route.query).length > 0;
            if (hasUrlFilters) {
                restoreFiltersFromUrl();
            }

            // Initialize pagination (single API call with restored filters)
            await initialize();

            // Watch for future route changes (back/forward buttons)
            watchRouteChanges();
        });

        return {
            datepickerPreviewFormat: Formatter.defaultDatepickerPreviewFormat,
            formLabelText: GrossProfitLabelTextHelper.getLabelText,
            CommonLabelTextHelper,
            actionHeaderItem: GrossProfitHeaderHelper.getActionHeaderItem(),
            headers: [...GrossProfitHeaderHelper.getHeaders()],
            grossProfitItemHeaders: [...GrossProfitItemHeaderHelper.getHeaders()],
            courierCodeList: [],
            data,
            // Pagination
            pagination,
            currentPageData,
            errorMessage,
            loadPage,
            resetPagination,
            nextPage,
            previousPage,
            selectPage,
            changePageSize,
            removeItemFromAllPages,
            updateItemInAllPages,
            // URL Filters
            syncFiltersToUrl
        };
    },
    computed: {},
    methods: {
        onKeydownQuickSearch(evt: any) {
            if (evt.key === 'Enter') {
                // Quick search is now handled by GenericListTable component
            }
        },
        async onResetClick() {
            this.data.searchFilterDateRangeGrp = _.cloneDeep(this.data.searchFilterDateRangeGrpDefault);

            // Clear URL query params
            await this.syncFiltersToUrl({});

            // Reload with defaults
            this.resetPagination();
            await this.loadPage(1, true);
        },
        async onSearchClick() {
            if (this.data.validation.hasErrors) {
                this.data.errorAlertMessage = 'Please fix the date range errors before searching';
                this.errorAlert?.showAlert();
                return;
            }

            // Flatten filters for URL (handle nested date ranges)
            const currentFilters = {
                dateOrderOnFrom: this.data.searchFilterDateRangeGrp.dateOrderOn.from,
                dateOrderOnTo: this.data.searchFilterDateRangeGrp.dateOrderOn.to
            };

            // Sync to URL BEFORE API call
            await this.syncFiltersToUrl(currentFilters);

            // Reset pagination and search with new filters
            this.resetPagination();
            await this.loadPage(1, true);
        },
        onDateRangeValidationChange(validationStatus: { hasErrors: boolean }) {
            this.data.validation.hasErrors = validationStatus.hasErrors;
        },
        async onImportClick() {
            await this.$router.push({ path: PathDefinition.calculateGrossProfit });
        },
        csvFormatter: function (value: unknown, key: string, row: Record<string, unknown>): unknown {
            return CsvFormatHelper.formatDateTimeCsvValue(value as string, key);
        },
        onPageSizeChange() {
            this.changePageSize();
        },
        onPageSelect(page: number) {
            this.selectPage(page);
        },
        onNextPage() {
            this.nextPage();
        },
        onPreviousPage() {
            this.previousPage();
        },

        async onViewGrossProfitItemsClick(item: any) {
            this.data.grossProfitItemDialog.selectedGrossProfit = item;
            this.data.grossProfitItemDialog.showDialog = true;
            this.data.grossProfitItemDialog.isLoading = true;
            this.data.grossProfitItemDialog.items = [];

            try {
                const grossProfitItems = await this.fetchAllGrossProfitItems(item.grossProfitId);
                this.data.grossProfitItemDialog.items = grossProfitItems;
            } catch (error) {
                this.handleError(error, 'Error fetching item-level gross profit data');
                this.data.grossProfitItemDialog.items = [];
            } finally {
                this.data.grossProfitItemDialog.isLoading = false;
            }
        },

        async fetchAllGrossProfitItems(grossProfitId: string) {
            const allItems: any[] = [];
            let page = 1;
            let hasNext = true;

            while (hasNext) {
                const requestUrl = `/api/staff/grossProfitItem/list?grossProfitId=${grossProfitId}&page=${page}&limit=100`;
                const response = await fetchWrapper.get(requestUrl, '');

                if (!response.success) {
                    throw response.errors || 'Failed to fetch item-level gross profit data';
                }

                const result = response.result || {};
                const pageItems = result.data || [];
                allItems.push(...pageItems);
                hasNext = result.pagination?.hasNext === true;
                page += 1;
            }

            return allItems;
        },

        onCloseGrossProfitItemsDialog() {
            this.data.grossProfitItemDialog.showDialog = false;
            this.data.grossProfitItemDialog.selectedGrossProfit = null;
            this.data.grossProfitItemDialog.items = [];
        },

        async onGetOrderItemSkusClick(item: any) {
            try {
                const orderId = item.orderId;
                const requestUrl = `/api/staff/marketplaceOrderItem/getByOrderId?orderId=${orderId}`;
                const response = await fetchWrapper.get(requestUrl, '');

                this.handleApiResponse(
                    response,
                    () => {
                        // Extract SKUs from order items and join them with line breaks
                        const orderItems = response.result || [];
                        const skus = orderItems.map((orderItem: any) => orderItem.sku).filter(Boolean);
                        const skuDisplay = skus.length > 0 ? skus.join('\n') : 'No item found';
                        this.updateItemInAllPages(item.grossProfitId, 'grossProfitId', 'orderItemSkuList', skuDisplay);
                    },
                    'Failed to fetch order item SKUs'
                );

                // If response fails, update with default value
                if (!response.success) {
                    this.updateItemInAllPages(item.grossProfitId, 'grossProfitId', 'orderItemSkuList', 'No item found');
                }
            } catch (error) {
                this.handleError(error, 'Error fetching order item SKUs', false);
                this.updateItemInAllPages(item.grossProfitId, 'grossProfitId', 'orderItemSkuList', 'No item found');
            }
        },

        // Utility method for handling errors consistently
        handleError(error: any, message: string, showAlert: boolean = true) {
            console.error(message, error);
            if (showAlert) {
                this.data.errorAlertMessage = typeof error === 'string' ? error : message;
                this.$refs.errorAlert.showAlert();
            }
        },

        // Utility method for handling API responses
        handleApiResponse(response: any, successCallback: () => void, errorMessage?: string) {
            if (response.success) {
                successCallback();
            } else {
                const message = errorMessage || response.errors || 'Operation failed';
                this.handleError(response.errors, message);
            }
        },

        onDeleteGrossProfitClick(item: any) {
            this.data.deleteOperation.selectedGrossProfit = item;
            this.data.deleteOperation.showConfirmDialog = true;
        },

        onCancelDelete() {
            this.data.deleteOperation.showConfirmDialog = false;
            this.data.deleteOperation.selectedGrossProfit = null;
        },

        async onConfirmDelete() {
            if (!this.data.deleteOperation.selectedGrossProfit) {
                return;
            }

            this.data.deleteOperation.isLoading = true;

            try {
                const grossProfitId = this.data.deleteOperation.selectedGrossProfit.grossProfitId;
                const requestUrl = `/api/staff/grossProfit/batchDelete`;
                const requestData = {
                    grossProfitIdList: [grossProfitId]
                };

                const response = await fetchWrapper.post(requestUrl, requestData);

                this.handleApiResponse(
                    response,
                    () => {
                        // Remove the item using the pagination composable
                        this.removeItemFromAllPages(grossProfitId, 'grossProfitId');

                        // Close dialog and reset state
                        this.data.deleteOperation.showConfirmDialog = false;
                        this.data.deleteOperation.selectedGrossProfit = null;
                    },
                    'Failed to delete gross profit'
                );
            } catch (error) {
                this.handleError(error, 'An unexpected error occurred while deleting the gross profit');
            } finally {
                this.data.deleteOperation.isLoading = false;
            }
        }
    }
});
</script>

<style>
.validation-error {
    color: red;
}
</style>
